import { createRoot } from 'react-dom/client'
import App from './App'
import { GlobalErrorBoundary } from './errors/AppErrorBoundary';
import './index.css'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { SpeedInsights } from "@vercel/speed-insights/react"
import { registerAppServiceWorker } from './lib/registerAppServiceWorker';
import { authDebug, isAuthDebugEnabled } from './lib/authDebugInstrumentation';

// QueryClient con defaults recomendados
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 60_000,
			gcTime: 5 * 60_000,
			retry: 3,
			refetchOnWindowFocus: false,
		},
	},
});

function getNavigationType(): string | null {
	try {
		const entry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
		return entry?.type ?? null;
	} catch {
		return null;
	}
}

function logRootLifecycle(event: string, payload: Record<string, unknown> = {}): void {
	authDebug(`RootLifecycle.${event}`, {
		...payload,
		visibilityState: typeof document !== 'undefined' ? document.visibilityState : null,
		navigationType: getNavigationType(),
		hrefLenApprox: typeof window !== 'undefined' ? window.location.href.length : 0,
		now: Date.now(),
	});
}

function installRootLifecycleDiagnostics(): void {
	if (typeof window === 'undefined' || !isAuthDebugEnabled()) return;

	const simpleEvents = ['beforeunload', 'popstate', 'hashchange'] as const;
	for (const eventName of simpleEvents) {
		window.addEventListener(eventName, () => logRootLifecycle(eventName));
	}

	window.addEventListener('pagehide', (event) => {
		logRootLifecycle('pagehide', { persisted: event.persisted });
	});
	window.addEventListener('pageshow', (event) => {
		logRootLifecycle('pageshow', { persisted: event.persisted });
	});
	document.addEventListener('visibilitychange', () => {
		logRootLifecycle('visibilitychange');
	});
	window.addEventListener('error', (event) => {
		logRootLifecycle('error', {
			message: event.message,
			filenamePresent: Boolean(event.filename),
			lineno: event.lineno,
			colno: event.colno,
		});
	});
	window.addEventListener('unhandledrejection', (event) => {
		const reason = event.reason;
		logRootLifecycle('unhandledrejection', {
			reasonName: reason?.name ?? null,
			reasonMessage: reason?.message ?? String(reason ?? ''),
		});
	});
}

installRootLifecycleDiagnostics();
logRootLifecycle('main_module_eval');

const rootElement = document.getElementById("root")!;
authDebug('ReactRoot.createRoot.start', { hasRootElement: Boolean(rootElement) });
const root = createRoot(rootElement);
authDebug('ReactRoot.createRoot.done');
authDebug('ReactRoot.render.start');

root.render(
	<QueryClientProvider client={queryClient}>
		<GlobalErrorBoundary>
			<App />
			{import.meta.env.PROD && <SpeedInsights />}
		</GlobalErrorBoundary>
	</QueryClientProvider>
);

authDebug('ReactRoot.render.called');

// Register a tiny Service Worker for runtime caching of property JSON and images (production only)
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
	window.addEventListener('load', () => {
		authDebug('ServiceWorker.register_on_load.start');
		registerAppServiceWorker().catch(() => {});
	});
}
